import React, { useContext, useEffect } from "react";

type ClassValue = string | false | null | undefined;

const cx = (...classes: ClassValue[]) => classes.filter(Boolean).join(" ");

type Variant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "dark"
  | "light"
  | "link"
  | string;

type Size = "sm" | "lg" | string;

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: React.ElementType;
  href?: string;
  variant?: Variant;
  size?: Size;
  active?: boolean;
};

export const Button = React.forwardRef<any, ButtonProps>(
  (
    {
      as,
      className,
      variant = "primary",
      size,
      active,
      children,
      href,
      type,
      ...props
    },
    ref,
  ) => {
    const Component = as || (href ? "a" : "button");
    const nextProps: any = {
      ...props,
      href,
      ref,
      className: cx(
        "btn",
        variant && `btn-${variant}`,
        size && `btn-${size}`,
        active && "active",
        className,
      ),
    };

    if (Component === "button") {
      nextProps.type = type || "button";
    }

    return React.createElement(Component as any, nextProps, children);
  },
);

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  border?: Variant;
};

const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, border, children, ...props }, ref) => (
    <div
      {...props}
      ref={ref}
      className={cx("card", border && `border-${border}`, className)}
    >
      {children}
    </div>
  ),
);

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("card-header", className)} />
  ),
);

const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("card-body", className)} />
  ),
);

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("card-footer", className)} />
  ),
);

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 {...props} ref={ref} className={cx("card-title", className)} />
  ),
);

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
  Title: CardTitle,
});

type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: Variant;
};

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = "primary", role = "alert", ...props }, ref) => (
    <div
      {...props}
      ref={ref}
      role={role}
      className={cx("alert", variant && `alert-${variant}`, className)}
    />
  ),
);

type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  variant?: Variant;
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = "primary", ...props }, ref) => (
    <span
      {...props}
      ref={ref}
      className={cx("badge", variant && `badge-${variant}`, className)}
    />
  ),
);

const BreadcrumbRoot = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, children, ...props }, ref) => (
    <nav {...props} ref={ref} aria-label="breadcrumb">
      <ol className={cx("breadcrumb", className)}>{children}</ol>
    </nav>
  ),
);

type BreadcrumbItemProps = React.LiHTMLAttributes<HTMLLIElement> & {
  active?: boolean;
  href?: string;
};

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ active, className, children, href, ...props }, ref) => (
    <li
      {...props}
      ref={ref}
      aria-current={active ? "page" : undefined}
      className={cx("breadcrumb-item", active && "active", className)}
    >
      {href && !active ? <a href={href}>{children}</a> : children}
    </li>
  ),
);

export const Breadcrumb = Object.assign(BreadcrumbRoot, {
  Item: BreadcrumbItem,
});

export const Container = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("container", className)} />
  ),
);

type RowProps = React.HTMLAttributes<HTMLDivElement>;

export const Row = React.forwardRef<HTMLDivElement, RowProps>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("row", className)} />
  ),
);

const colWidthClass = (prefix: string, value?: number | string | boolean) => {
  if (value === undefined || value === false) return "";
  if (value === true) return prefix ? `col-${prefix}` : "col";
  const key = String(value);
  return prefix ? `col-${prefix}-${key}` : `col-${key}`;
};

type ColProps = React.HTMLAttributes<HTMLDivElement> & {
  xs?: number | string | boolean;
  sm?: number | string | boolean;
  md?: number | string | boolean;
  lg?: number | string | boolean;
  xl?: number | string | boolean;
};

export const Col = React.forwardRef<HTMLDivElement, ColProps>(
  ({ className, xs, sm, md, lg, xl, ...props }, ref) => (
    <div
      {...props}
      ref={ref}
      className={cx(
        "col min-w-0",
        colWidthClass("", xs),
        colWidthClass("sm", sm),
        colWidthClass("md", md),
        colWidthClass("lg", lg),
        colWidthClass("xl", xl),
        className,
      )}
    />
  ),
);

const FormRoot = React.forwardRef<HTMLFormElement, React.FormHTMLAttributes<HTMLFormElement>>(
  ({ className, ...props }, ref) => (
    <form {...props} ref={ref} className={cx(className)} />
  ),
);

type FormControlElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

type FormControlProps =
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "as" | "size" | "onChange"> &
  Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "as" | "size" | "onChange"> &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "as" | "size" | "onChange"> & {
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  isInvalid?: boolean;
  plaintext?: boolean;
  onChange?: React.ChangeEventHandler<FormControlElement>;
};

const FormControl = React.forwardRef<any, FormControlProps>(
  ({ as, className, isInvalid, plaintext, children, ...props }, ref) => {
    const Component = as || "input";

    return React.createElement(
      Component as any,
      {
        ...props,
        ref,
        className: cx(
          plaintext ? "form-control-plaintext" : "form-control",
          isInvalid && "is-invalid",
          className,
        ),
      },
      children,
    );
  },
);

const FormGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("form-group", className)} />
  ),
);

const FormLabel = React.forwardRef<HTMLLabelElement, React.LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <label {...props} ref={ref} className={cx("form-label", className)} />
  ),
);

type FormCheckProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label?: React.ReactNode;
  inline?: boolean;
};

const FormCheck = React.forwardRef<HTMLInputElement, FormCheckProps>(
  ({ className, label, inline, id, type = "checkbox", ...props }, ref) => {
    const input = (
      <input
        {...props}
        ref={ref}
        id={id}
        type={type}
        className={cx("form-check-input", className)}
      />
    );

    if (!label) {
      return <div className={cx("form-check", inline && "form-check-inline")}>{input}</div>;
    }

    return (
      <div className={cx("form-check", inline && "form-check-inline")}>
        {input}
        <label className="form-check-label" htmlFor={id}>
          {label}
        </label>
      </div>
    );
  },
);

const FormText = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <small {...props} ref={ref} className={cx("form-text", className)} />
  ),
);

export const Form = Object.assign(FormRoot, {
  Control: FormControl,
  Group: FormGroup,
  Label: FormLabel,
  Check: FormCheck,
  Text: FormText,
});

type TableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  striped?: boolean;
  bordered?: boolean;
  borderless?: boolean;
  hover?: boolean;
  size?: Size;
};

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      striped,
      bordered,
      borderless,
      hover,
      size,
      ...props
    },
    ref,
  ) => (
    <div className="table-responsive">
      <table
        {...props}
        ref={ref}
        className={cx(
          "table",
          striped && "table-striped",
          bordered && "table-bordered",
          borderless && "table-borderless",
          hover && "table-hover",
          size && `table-${size}`,
          className,
        )}
      />
    </div>
  ),
);

type SpinnerProps = React.HTMLAttributes<HTMLSpanElement> & {
  animation?: "border" | "grow" | string;
  size?: Size;
  variant?: Variant;
};

export const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  (
    {
      className,
      animation = "border",
      size,
      variant,
      role = "status",
      ...props
    },
    ref,
  ) => {
    void animation;
    void variant;

    return (
      <span
        {...props}
        ref={ref}
        role={role}
        className={cx("dream-spinner", size && "dream-spinner-sm", className)}
      >
        <img src="/images/dream-loader.gif" alt="" aria-hidden="true" />
      </span>
    );
  },
);

type ModalContextValue = {
  onHide?: () => void;
};

const ModalContext = React.createContext<ModalContextValue>({});

type ModalProps = {
  show?: boolean;
  onHide?: () => void;
  keyboard?: boolean;
  centered?: boolean;
  size?: "sm" | "lg" | "xl" | string;
  children?: React.ReactNode;
  className?: string;
  dialogClassName?: string;
};

const ModalRoot = ({
  show,
  onHide,
  keyboard = true,
  centered,
  size,
  children,
  className,
  dialogClassName,
}: ModalProps) => {
  useEffect(() => {
    if (!show || !keyboard) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onHide?.();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [keyboard, onHide, show]);

  useEffect(() => {
    if (!show) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      className={cx("tw-modal-backdrop", className)}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onHide?.();
      }}
    >
      <div
        className={cx(
          "tw-modal-dialog",
          centered && "tw-modal-dialog-centered",
          size && `tw-modal-${size}`,
          dialogClassName,
        )}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <ModalContext.Provider value={{ onHide }}>
          <div className="tw-modal-content">{children}</div>
        </ModalContext.Provider>
      </div>
    </div>
  );
};

type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  closeButton?: boolean;
};

const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, closeButton, children, ...props }, ref) => {
    const { onHide } = useContext(ModalContext);

    return (
      <div {...props} ref={ref} className={cx("modal-header", className)}>
        {children}
        {closeButton && (
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onHide}
          >
            x
          </button>
        )}
      </div>
    );
  },
);

const ModalTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 {...props} ref={ref} className={cx("modal-title", className)} />
  ),
);

const ModalBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("modal-body", className)} />
  ),
);

const ModalFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("modal-footer", className)} />
  ),
);

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
  Footer: ModalFooter,
});

type NavContextValue = {
  activeKey?: string;
  onSelect?: (eventKey: string | null) => void;
};

const NavContext = React.createContext<NavContextValue>({});

type NavProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> & {
  activeKey?: string;
  onSelect?: (eventKey: string | null) => void;
  as?: React.ElementType;
};

const NavRoot = React.forwardRef<any, NavProps>(
  ({ as, activeKey, onSelect, className, children, ...props }, ref) => {
    const Component = as || "div";

    return (
      <NavContext.Provider value={{ activeKey, onSelect }}>
        {React.createElement(
          Component as any,
          {
            ...props,
            ref,
            className: cx("nav", className),
          },
          children,
        )}
      </NavContext.Provider>
    );
  },
);

const NavItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx("nav-item", className)} />
  ),
);

type NavLinkProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  eventKey?: string;
};

const NavLink = React.forwardRef<HTMLButtonElement, NavLinkProps>(
  ({ className, eventKey, onClick, children, type, ...props }, ref) => {
    const { activeKey, onSelect } = useContext(NavContext);
    const isActive = eventKey !== undefined && activeKey === eventKey;

    return (
      <button
        {...props}
        ref={ref}
        type={type || "button"}
        className={cx("nav-link", isActive && "active", className)}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) {
            onSelect?.(eventKey || null);
          }
        }}
      >
        {children}
      </button>
    );
  },
);

export const Nav = Object.assign(NavRoot, {
  Item: NavItem,
  Link: NavLink,
});
